import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Briefcase,
  Calendar,
  Clock,
  MapPin,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import CareerJobBody from '../components/Careers/CareerJobBody';
import MarketingPageFooter from '../components/marketing/MarketingPageFooter';
import MarketingLandingNav, { MARKETING_NAV_TOP_OFFSET } from '../components/marketing/MarketingLandingNav';
import { supabase } from '../lib/supabase';
import { SEO } from '../shared/components/SEO';
import { buildJobPostingJsonLd } from '../utils/careerJobPostingSchema';

interface CareerPost {
  id: string;
  slug: string;
  title: string;
  meta_title: string | null;
  meta_description: string | null;
  department: string | null;
  location: string | null;
  job_type: string | null;
  experience_level: string | null;
  salary_range: string | null;
  hero_headline: string | null;
  hero_intro: string | null;
  description: string | null;
  requirements: string[] | null;
  nice_to_have: string[] | null;
  benefits: string[] | null;
  application_url: string | null;
  published_at: string | null;
  closes_at: string | null;
}

interface CareerPostPageProps {
  slug?: string;
  onNavigate?: (view: string) => void;
}

function getSlugFromUrl(): string {
  const m = window.location.pathname.match(/^\/careers\/([^/]+)/);
  return m ? m[1] : '';
}

const CareerPostPage: React.FC<CareerPostPageProps> = ({ slug: propSlug, onNavigate }) => {
  const [post, setPost] = useState<CareerPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const slug = propSlug || getSlugFromUrl();

  useEffect(() => {
    if (!slug) { setError('Job not found'); setLoading(false); return; }
    (async () => {
      const { data, error: err } = await supabase
        .from('career_posts')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'published')
        .single();
      if (err || !data) { setError('Job opening not found or no longer available.'); }
      else { setPost(data as CareerPost); }
      setLoading(false);
    })();
  }, [slug]);

  const prerenderSignaledRef = React.useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!(window as { __GGJ_PRERENDER__?: boolean }).__GGJ_PRERENDER__) return;
    if (loading) return;
    if (prerenderSignaledRef.current) return;
    prerenderSignaledRef.current = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.dispatchEvent(new Event('ggj-prerender-ready'));
      });
    });
  }, [loading, error, post]);

  const jobPostingLd = useMemo(
    () => (post ? buildJobPostingJsonLd(post) : undefined),
    [post],
  );

  return (
    <div className="careers-marketing-page min-h-screen flex flex-col transition-colors duration-300">
      {post && (
        <SEO
          title={post.meta_title || `${post.title} — GoGrabJob Careers`}
          description={post.meta_description || post.hero_intro || `Apply for ${post.title} at GoGrabJob.`}
          canonical={`https://www.gograbjob.com/careers/${post.slug}`}
          jsonLd={jobPostingLd}
        />
      )}

      <MarketingLandingNav onNavigate={onNavigate} activeView="careers" variant="marketing" />

      {/* Hero */}
      <section className={`careers-marketing-hero ${MARKETING_NAV_TOP_OFFSET}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <button
            onClick={() => onNavigate?.('careers')}
            className="flex items-center gap-1.5 text-stone-600 dark:text-stone-400 hover:text-[#15803d] text-sm mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> All Openings
          </button>

          {loading ? (
            <div className="animate-pulse space-y-3">
              <div className="h-6 w-24 bg-white/20 rounded-full" />
              <div className="h-10 w-3/4 bg-white/20 rounded-xl" />
              <div className="h-5 w-1/2 bg-white/20 rounded-lg" />
            </div>
          ) : error ? (
            <div className="text-white text-lg">{error}</div>
          ) : post && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
              <p className="home-problem-section__eyebrow">Open role</p>
              {post.department && (
                <span className="careers-dept-pill inline-block mb-4">
                  {post.department}
                </span>
              )}
              <h1 className="home-problem-section__title text-left !mx-0 !max-w-none text-3xl sm:text-4xl mb-3">{post.title}</h1>
              {post.hero_headline && (
                <p className="home-problem-section__life-title text-left !text-lg mb-3">{post.hero_headline}</p>
              )}
              {post.hero_intro && (
                <p className="home-problem-section__subtitle text-left !mx-0 !max-w-2xl mb-5">{post.hero_intro}</p>
              )}
              <div className="flex flex-wrap gap-4 text-stone-600 dark:text-stone-400 text-sm">
                {post.location      && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{post.location}</span>}
                {post.job_type      && <span className="flex items-center gap-1.5"><Briefcase className="w-4 h-4" />{post.job_type}</span>}
                {post.experience_level && <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{post.experience_level}</span>}
                {post.closes_at && <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" />Closes {new Date(post.closes_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</span>}
              </div>
              {post.salary_range && (
                <p className="mt-3 text-[#15803d] dark:text-[#86efac] font-semibold">{post.salary_range}</p>
              )}
            </motion.div>
          )}
        </div>
      </section>

      {/* Body */}
      {!loading && !error && post && (
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            {/* Content */}
            <div className="lg:col-span-2">
              <CareerJobBody
                description={post.description}
                requirements={post.requirements}
                niceToHave={post.nice_to_have}
                benefits={post.benefits}
              />
            </div>

            {/* Apply sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-20 space-y-4">
                <div className="home-problem-section__mesh careers-listing-mesh">
                  <div className="hiw-mesh-layer" aria-hidden />
                  <div className="home-problem-section__mesh-inner">
                  <h3 className="home-problem-section__life-title careers-job-section__title !text-base mb-4">Apply for this role</h3>

                  {post.application_url ? (
                    <a
                      href={post.application_url.startsWith('mailto:') || post.application_url.startsWith('http') ? post.application_url : `mailto:${post.application_url}`}
                      target={post.application_url.startsWith('http') ? '_blank' : undefined}
                      rel="noopener noreferrer"
                      className="careers-marketing-btn-primary w-full"
                    >
                      Apply Now <ArrowRight className="w-4 h-4" />
                    </a>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        const path = `/careers/${post.slug}/apply`;
                        window.history.pushState({}, '', path);
                        window.dispatchEvent(new PopStateEvent('popstate'));
                      }}
                      className="careers-marketing-btn-primary w-full"
                    >
                      Apply Now <ArrowRight className="w-4 h-4" />
                    </button>
                  )}

                  <div className="mt-5 space-y-3 text-sm">
                    {[
                      [Briefcase, post.job_type],
                      [MapPin, post.location],
                      [Clock, post.experience_level],
                    ].filter(([, v]) => v).map(([Icon, value], i) => {
                      const Ic = Icon as React.FC<{ className?: string }>;
                      return (
                        <div key={i} className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                          <Ic className="w-4 h-4 shrink-0 text-gray-400 dark:text-gray-500" />
                          {value as string}
                        </div>
                      );
                    })}
                    {post.closes_at && (
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                        <Calendar className="w-4 h-4 shrink-0 text-gray-400" />
                        Deadline: {new Date(post.closes_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </div>
                    )}
                  </div>

                  </div>
                </div>

                <button
                  onClick={() => onNavigate?.('careers')}
                  className="w-full py-2.5 border border-stone-200 dark:border-stone-600 rounded-xl text-sm text-stone-600 dark:text-stone-400 hover:border-[#15803d] hover:text-[#15803d] transition-colors"
                >
                  ← View all openings
                </button>
              </div>
            </div>
          </div>
        </main>
      )}

      <MarketingPageFooter onNavigate={onNavigate as any} theme="careers" />
    </div>
  );
};

export default CareerPostPage;
